/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Spin,
  Row,
  Col,
  Modal,
  TreeSelect,
  Button,
} from 'antd';
import Table from 'components/Table';
import ModalDelete from 'components/ModalDelete';
import { checkToken } from 'api/utils';
import { areaBuscar, normalizeData } from 'utils/normalizers';
import { formatReceived } from 'utils/formatValues';
import FormSubmitControls from 'components/FormSubmitControls';
import { onError } from 'utils/handlers';
import {
  getCuentaContableISN,
  getCuentasContablesISN,
  patchCuentaContableISN,
  postCuentaContableISN,
  deleteCuentaContableISN,
} from 'api/nominas/cuentasContablesISN';
import {
  getEstadosGlobales,
  getAreas,
  getContentTypesUR,
  getCuentasContables,
} from 'api/catalogos/index';
import Select from 'components/Select';
import makeTree from 'utils/makeTreeFromCuentasContables';
import { ESTATUS_AUTORIZADO } from 'utils/constants';

export const cuentaContableISNPermission = {
  permissionModel: 'cuentacontableisn',
  catalogsModels: [],
  tabsModels: [],
};

const TarifasISNCuentasContables = ({
  tarifaId,
  rules,
  permission,
  disableEditar,
}) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [areas, setAreas] = useState();
  const [contentTypes, setContentTypes] = useState([]);
  const [cuentasContables, setCuentasContables] = useState([]);
  const [estados, setEstados] = useState([]);

  const formInstances = [
    form,
  ];

  const calcularProfundidadMaxima = (arreglo) => {
    let maxProfundidad = 0;
    const calcularProfundidadRecursiva = (nodo, profundidad) => {
      maxProfundidad = Math.max(maxProfundidad, profundidad);
      if (!nodo.children || nodo.children.length === 0) {
        return;
      }
      nodo.children.forEach((hijo) => {
        calcularProfundidadRecursiva(hijo, profundidad + 1);
      });
    };
    arreglo.forEach((nodo) => {
      calcularProfundidadRecursiva(nodo, 1);
    });
    return maxProfundidad;
  };

  const agregarDisabledEnNivelMasProfundo = (arreglo) => {
    const profundidadMaxima = calcularProfundidadMaxima(arreglo);
    const agregarDisabledRecursivo = (nodo, profundidadActual) => {
      if (!nodo.children || nodo.children.length === 0) {
        if (profundidadActual === profundidadMaxima) {
          // eslint-disable-next-line no-param-reassign
          nodo.disabled = false;
          return;
        }
        // eslint-disable-next-line no-param-reassign
        nodo.disabled = true;
        return;
      }
      if (profundidadActual === profundidadMaxima) {
        // eslint-disable-next-line no-param-reassign
        nodo.disabled = false;
      } else {
        // eslint-disable-next-line no-param-reassign
        nodo.disabled = true;
      }
      nodo.children.forEach((hijo) => {
        agregarDisabledRecursivo(hijo, profundidadActual + 1);
      });
    };
    arreglo.forEach((nodo) => {
      agregarDisabledRecursivo(nodo, 1);
    });
  };

  const normalizeCuentasContables = (_data) => {
    if (_data) {
      const output = _data.map((item) => {
        const clone = { ...item };
        const children = normalizeCuentasContables(clone.children);
        const hasCuentasChildren = children.some((e) => e.conac);
        const selectable = clone.tipo !== 7 && !hasCuentasChildren;
        if (!children.length) {
          delete clone.children;
          return ({
            ...clone,
            title: `${clone.clave_visible || clone.clave} - ${clone.nombre}`,
            value: clone.id,
            key: clone.id,
            disabled: !selectable,
          });
        }
        return ({
          ...clone,
          title: `${clone.clave_visible || clone.clave} - ${clone.nombre}`,
          value: clone.id,
          key: clone.id,
          children,
          disabled: !selectable,
        });
      });
      return output;
    }
    return [];
  };

  const flattenWithChildren = (arr) => {
    const flattened = [];
    const traverse = (items) => {
      items.forEach((item) => {
        const { children, ...itemWithoutChildren } = item;
        flattened.push(itemWithoutChildren);
        if (children && children.length > 0) {
          traverse(children);
        }
      });
    };
    traverse(arr);
    return flattened;
  };

  const fetchData = async () => {
    setLoading(true);
    const _data = await getCuentasContablesISN();
    setData(formatReceived(_data?.filter((e) => e.tarifa_isn === tarifaId)));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _cuentasContables,
        _estadosGlobales,
        _areas,
        _contentTypes,
        _data,
      ] = await Promise.all([
        getCuentasContables(),
        getEstadosGlobales(),
        getAreas({ estados_globales: ESTATUS_AUTORIZADO }),
        getContentTypesUR(),
        getCuentasContablesISN(),
      ]);
      const formattedURS = normalizeData(_areas);
      if (mounted) {
        const normalizedCuentas = normalizeCuentasContables(makeTree(_cuentasContables));
        agregarDisabledEnNivelMasProfundo(normalizedCuentas);
        setCuentasContables(flattenWithChildren(normalizedCuentas));
        setAreas(formattedURS);
        setContentTypes(_contentTypes);
        setData(formatReceived(_data?.filter((e) => e.tarifa_isn === tarifaId)));
        setEstados(_estadosGlobales?.filter((e) => e.id === 1 || e.id === 3));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setSelectedRowKeys([]);
    setVisible(false);
    form.resetFields();
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _cuentaContable = await getCuentaContableISN(match.id);
    const areaValue = areaBuscar(_cuentaContable?.area_content_id, areas);
    _cuentaContable.unidad_responsable = areaValue?.value;
    setTimeout(() => {
      if (_cuentaContable) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceived(_cuentaContable));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const getURValuesToSend = () => {
    const unidad_responsable = form.getFieldValue('unidad_responsable');
    if (unidad_responsable) {
      const [_area, _nivel] = unidad_responsable.split('/');
      const area_content_id = contentTypes.find((ct) => ct.nivel.toString() === _nivel).id;
      return { _area, area_content_id };
    }
    return { _area: undefined, area_content_id: undefined };
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const contentValues = getURValuesToSend();
      const values = {
        ...form.getFieldsValue(),
        area: contentValues?.area_content_id,
        area_content_id: contentValues?._area,
      };
      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len
      const saved = await (key ? patchCuentaContableISN(key, { ...values, tarifa_isn: tarifaId }) : postCuentaContableISN({ ...values, tarifa_isn: tarifaId }));
      if (saved) {
        if (_continue) {
          await fetchData();
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteCuentaContableISN(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'Área',
      dataIndex: 'area_content_id',
      key: 'area_content_id',
      width: 150,
      render: (val) => areaBuscar(val, areas)?.title,
    },
    {
      titleText: 'Cuenta Contable',
      dataIndex: 'cuenta_contable',
      key: 'cuenta_contable',
      width: 300,
      render: (val) => {
        const _cuentaContable = cuentasContables.find((e) => e.id === val);
        return `${_cuentaContable?.clave_visible || _cuentaContable?.clave} - ${_cuentaContable?.nombre}`;
      },
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 100,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd: () => onClickAdd(),
            onClickEdit: () => onClickEdit(),
            onClickDelete: () => onClickDelete(),
          }}
          permission={permission}
          disableAdd={disableEditar}
          disableDelete={disableEditar}
          allowExportPDF={false}
        />
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content="Cuenta Contable"
          loading={loading}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Cuenta Contable`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              allowSave={!disableEditar}
            />
          )}
          onCancel={onCancel}
          forceRender
          width="40%"
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrolltofirsterror
            name="general"
          >
            <Row gutter={10}>
              <Col xs={24} sm={24} md={24}>
                <Form.Item
                  name="unidad_responsable"
                  label="Área"
                  rules={rules.required}
                  hasFeedback
                >
                  <TreeSelect
                    showSearch
                    treeNodeFilterProp="title"
                    style={{ width: '100%' }}
                    dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                    treeData={areas}
                    disabled={disableEditar}
                  />
                </Form.Item>
                <Form.Item
                  name="cuenta_contable"
                  label="Cuenta Contable"
                  rules={rules.required}
                >
                  <Select
                    keyProp="clave"
                    labelProp="nombre"
                    keyLabelRender
                    dataSource={cuentasContables}
                    disabled={disableEditar}
                  />
                </Form.Item>
                <Form.Item
                  name="estados_globales"
                  label="Estatus"
                  rules={rules.estatus}
                  hasFeedback
                  initialValue={1}
                >
                  <Select
                    labelProp="descripcion"
                    keyProp="id"
                    dataSource={estados}
                    disabled={selectedRowKeys.length <= 0 || disableEditar}
                  />
                </Form.Item>
              </Col>
              <Form.Item>
                <Button htmlType="submit" />
              </Form.Item>
            </Row>
          </Form>
        </Modal>
      </Spin>
    </Row>
  );
};

export default TarifasISNCuentasContables;
